import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useContext, useRef, useMemo, useCallback, Fragment } from 'react';
import { doc, getDoc, collection, query, where, getDocs, addDoc, onSnapshot, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import ImageOverlay from '../components/ImageOverlay';
import { UserContext } from '../contexts/UserContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useContact } from '../contexts/ContactContext';
import ActiveTraderBadge from '../components/ActiveTraderBadge';
import ResponderBadge from '../components/ResponderBadge';
import WantDemandBadge from '../components/WantDemandBadge';
import ListingThumb from '../components/ListingThumb';
import { placeholderFor, isPlaceholderListing, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { isWeeklyActiveTrader } from '../utils/isWeeklyActiveTrader';
import { doesPedalFullyMatchWant, isExcludedByKeywords } from '../utils/matchingLogic';
import { findDuplicateWant, isSameNamedPedal } from '../utils/wantIdentity';
import ListPedalPromptModal from '../components/ListPedalPromptModal';
import SEO from '../components/SEO';
import PedalPriceDisplay, { OfferModePill } from '../components/PedalPriceDisplay';
import PriceIndicator from '../components/PriceIndicator';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from '../utils/offerIntentAnalytics';
import { recordListingView } from '../utils/listingViewTracking';
import { withTransientRetry } from '../utils/transientReadRetry';
import { armRecoveryOnReconnect, throwIfOfflineMiss } from '../utils/recoverableLoad';
import { validateMessage } from '../utils/profanityFilter';
import { formatMention, stripMentions, prependMention, replyTargetName } from '../utils/commentMentions';
import { useCommentParticipants } from '../hooks/useCommentParticipants';
import { useEdgeSwipeBack } from '../hooks/useSwipeViews';
import { isListingInterestEnabled } from '../featureFlags';
import { INTEREST_RESEND_AFTER_MS } from '../utils/listingInterestPolicy';
import { reloadPage } from '../utils/reloadPage';
import { offerModeOf, allowsTrade, allowsPurchase } from '../utils/offerMode';
import AutoGrowTextarea from '../components/AutoGrowTextarea';
import { formatRelativeTime } from '../utils/relativeTime';
import {
  quickAddWantForListing,
  quickRemoveWantForListing,
  sendInterestForListing,
  signedOutWantPrefillUrl
} from '../utils/quickWant';

const PedalDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser } = useContext(UserContext);
  const { notifications, markWantListMatchesAsRead } = useNotifications();
  const { openContact } = useContact();
  const [pedal, setPedal] = useState(null);
  // Phase 2b (2026-09-29): PedalDetails used to render "Loading pedal
  // details..." FOREVER for both a failed load and a deleted listing.
  // Three honest terminal states instead: loaded, 'failed' (couldn't load —
  // Retry + one-shot reconnect recovery), 'notfound' (SERVER-confirmed
  // missing only; an offline cache-miss is classified as failed, never as
  // deleted). detailRetryNonce re-runs the load effect for Retry/recovery.
  const [detailLoadProblem, setDetailLoadProblem] = useState(null); // null | 'failed' | 'notfound'
  const [detailRetryNonce, setDetailRetryNonce] = useState(0);
  // R5: the load effect re-runs on detailRetryNonce, and each successful run
  // used to record another listing view — a Retry press or reconnect
  // recovery must not inflate view analytics. One view per listing id per
  // page visit (component instance); navigating to another listing (id
  // change) still records, exactly the pre-existing definition of a view.
  const viewRecordedIdRef = useRef(null);
  const [ownerInfo, setOwnerInfo] = useState({ username: '', badge: null });
  const [currentUser, setCurrentUser] = useState(null);
  const [overlayImage, setOverlayImage] = useState({ isOpen: false, url: '', alt: '', index: 0 });
  const [isInPurchaseOffer, setIsInPurchaseOffer] = useState(false);
  const [imageArray, setImageArray] = useState([]);

  // "Hey, I'm interested" (beta). Gated per-user because sending one notifies
  // the listing owner; see featureFlags.isListingInterestEnabled.
  const interestEnabled = isListingInterestEnabled(firestoreUser);
  const [interestSent, setInterestSent] = useState(false);
  const [interestAnswered, setInterestAnswered] = useState(false);
  // "List a pedal first" prompt — interest from an empty pedalboard goes
  // nowhere, so it's blocked with an encouragement instead (2026-08-20).
  const [showListPedalPrompt, setShowListPedalPrompt] = useState(false);
  // Does this user have anything listed? Interest points the owner at the
  // sender's pedalboard, so from an empty one the nudge is withheld and a note
  // under the button explains why — the Wantlist add itself still goes
  // through (Jason's call, 2026-08-23; the button is no longer disabled).
  // null = not known yet: show no note rather than flash one at a user who
  // does have listings. sendInterestForPedal keeps its own tap-time check,
  // which covers the null window and a stale count.
  const [hasActiveListing, setHasActiveListing] = useState(null);

  // Native-only iOS-style edge swipe-back to the previous screen (e.g. Browse,
  // which restores its own scroll position). Same destination as the "← Back"
  // button. Disabled while the fullscreen image overlay is open so it doesn't
  // interfere with the overlay's own image navigation.
  const {
    swipeProps: swipeBackProps,
    exitClass: swipeBackExitClass,
    dragClass: swipeBackDragClass,
  } = useEdgeSwipeBack(
    () => navigate(-1),
    // liveDrag makes the page follow the thumb, matching the My Pedals tabs, and
    // brings axis locking with it so the page can't slide sideways and scroll at
    // the same time. edgeGate off because gating to the left third made most of
    // the screen ignore the gesture, which read as broken next to every other
    // page — the global back-swipe in App.js is ungated for the same reason.
    // liveDrag already commits at COMMIT_PX, the same 60px that one uses.
    { enabled: !overlayImage.isOpen, edgeGate: false, liveDrag: true }
  );
  
  // Wantlist checking state
  // The user's want document that fully matches this listing (make+model+price+
  // condition+effectTypes all satisfied). Stored so the quick-remove path has the
  // doc id to delete. `isInWantList` is derived from this — there is no separate
  // state var to keep in sync.
  const [existingFullMatchWant, setExistingFullMatchWant] = useState(null);
  const isInWantList = !!existingFullMatchWant;
  const [checkingWantList, setCheckingWantList] = useState(true);
  // True while a quick add/remove write is in flight. Used to disable the
  // button so a fast double-tap can't fire a duplicate addDoc or a delete-then-
  // add race against a stale `existingFullMatchWant`.
  const [isMutatingWantList, setIsMutatingWantList] = useState(false);
  // Transient label shown on the button for ~1.5s after a successful quick-add
  // or quick-remove. Null when no feedback active. Lives separately from the
  // primary button state so the underlying state (`isInWantList`) flips
  // immediately on write success — only the label is briefly overridden.
  const [wantFeedback, setWantFeedback] = useState(null);
  // Want with same make/model (alias-aware) but different price/condition than this listing.
  // Drives the "you already have this in your Wantlist" modal so users don't get a
  // surprise alert from AddWantForm's duplicate guard after clicking "Save to Wantlist".
  const [existingWantSameModel, setExistingWantSameModel] = useState(null);
  // True when that want matched on exact make+model rather than the fuzzy name
  // check. Gates the "add it anyway" option, which only makes sense when the
  // match could be wrong.
  const [existingWantIsExact, setExistingWantIsExact] = useState(false);
  const [showWantConflictModal, setShowWantConflictModal] = useState(false);

  // Name the actual conflict rather than asserting a generic one. The modal used
  // to say "your current criteria do not match this listing" above a panel
  // showing only max price and min condition — which since the identity check
  // stopped requiring effect types to overlap is frequently a panel where both
  // read "Any" and nothing visibly conflicts at all. Effect types and excluded
  // keywords are now the most common reasons to land here, and neither was
  // mentioned anywhere.
  const wantConflict = useMemo(() => {
    if (!existingWantSameModel || !pedal) return null;

    const want = existingWantSameModel;
    const wantEffects = Array.isArray(want.effectTypes) ? want.effectTypes : [];
    const listingEffects = Array.isArray(pedal.effectTypes) ? pedal.effectTypes : [];
    const excluded = Array.isArray(want.excludedKeywords) ? want.excludedKeywords : [];

    // Name the want itself (Jason, 2026-09-18): the modal said "you have this
    // pedal in your Wantlist" without SHOWING which want, so a fuzzy-tier
    // match (e.g. "ProCo Rat" vs "ProCo Lil Rat") couldn't be sanity-checked.
    const wantName = [want.make, want.model].filter(Boolean).join(' ')
      || (wantEffects.length ? `${wantEffects.join(', ')} (effect-type want)` : 'Unnamed want');
    const identityRow = ['Your existing want', wantName];

    if (excluded.length > 0 && isExcludedByKeywords(want, pedal)) {
      return {
        reason: 'This listing matches a keyword you asked never to be shown, so your want skips it.',
        rows: [identityRow, ['Keywords you exclude', excluded.join(', ')]],
      };
    }

    if (
      wantEffects.length > 0 &&
      listingEffects.length > 0 &&
      !wantEffects.some((t) => listingEffects.includes(t))
    ) {
      return {
        reason: 'Your want is set to different effect types than this listing is tagged with.',
        rows: [
          identityRow,
          ['Your effect types', wantEffects.join(', ')],
          ['This listing', listingEffects.join(', ')],
        ],
      };
    }

    if (wantEffects.length > 0 && listingEffects.length === 0) {
      return {
        reason: "This listing isn't tagged with any effect type, so your want can't match it.",
        rows: [identityRow, ['Your effect types', wantEffects.join(', ')]],
      };
    }

    return {
      reason: 'Your current criteria do not match this listing.',
      rows: [
        identityRow,
        ['Your current max price', want.maxPrice ? `$${want.maxPrice}` : 'Any'],
        ['Your current min condition', want.condition || 'Any'],
      ],
    };
  }, [existingWantSameModel, pedal]);

  // Pedals of mine that this listing's owner wants (for "Wants your: ..." section)
  const [ownerWantsMyPedals, setOwnerWantsMyPedals] = useState([]);
  
  // Comment section state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(true);
  const [submittingComment, setSubmittingComment] = useState(false);
  // Shown under the comment box instead of a browser alert() (2026-10-23).
  const [commentError, setCommentError] = useState('');
  const commentsRef = useRef(null);
  const textareaRef = useRef(null);
  // Canonical usernames for this listing's thread (owner + commenters, by
  // uid from publicProfiles) and names picked from the @ popup. Scoped to
  // the listing id: see useCommentParticipants for why.
  const { participantUsernames, addPickedName, mentionExemptions } = useCommentParticipants(id, pedal, comments);
  
  // Autocomplete state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionSearchTerm, setMentionSearchTerm] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  
  // Check if coming from match details page or offer page
  const urlParams = new URLSearchParams(window.location.search);
  const isFromMatch = urlParams.get('from') === 'match';
  const isFromOffer = urlParams.get('fromOffer') === 'true';

  // Extract YouTube video ID from various URL formats
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  // Build image array when pedal data loads
  useEffect(() => {
    if (pedal?.images) {
      const images = [];
      const imageKeys = ['top', 'back', 'upper', 'lower', 'detail1', 'detail2', 'detail3', 'detail4'];
      const imageLabels = {
        top: 'Top View',
        back: 'Back View',
        upper: 'Upper View',
        lower: 'Lower View',
        detail1: 'Detail 1',
        detail2: 'Detail 2',
        detail3: 'Detail 3',
        detail4: 'Detail 4'
      };
      
      imageKeys.forEach(key => {
        if (pedal.images[key]) {
          images.push({
            url: pedal.images[key],
            alt: imageLabels[key],
            key: key
          });
        }
      });

      const ytId = getYouTubeId(pedal.youtubeUrl);
      if (ytId) {
        images.push({
          url: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`,
          alt: 'YouTube Demo',
          key: 'youtube',
          type: 'youtube',
          youtubeId: ytId
        });
      }
      
      setImageArray(images);
    }
  }, [pedal]);

  // Fetch and listen to comments
  useEffect(() => {
    if (!id) return;

    const commentsQuery = query(
      collection(db, 'listingComments'),
      where('listingId', '==', id),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      const fetchedComments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setComments(fetchedComments);
      setLoadingComments(false);
    }, (error) => {
      console.error('Error fetching comments:', error);
      setLoadingComments(false);
    });

    return () => unsubscribe();
  }, [id]);

  // Open every listing at the top, no matter where it was opened from (Browse,
  // Match Opportunities, Wantlist Matches, dashboard thumbnail, My Pedals
  // detail). React Router v6 keeps the prior scroll position, so without this
  // the listing can open mid-page. Keyed on `id` so it also resets when moving
  // between listings. Skipped when the URL deep-links to the comments section.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('scrollToComments') === 'true') return;
    window.scrollTo(0, 0);
  }, [id]);

  // Viewing the listing a want-list-match notification points to clears that
  // notification, dropping the Trading tab badge. Scoped to THIS listing only
  // (other pending want-list matches keep their badge). Re-runs on
  // `notifications` so a notification that loads a beat late still gets cleared.
  useEffect(() => {
    if (!id) return;
    markWantListMatchesAsRead(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, notifications]);

  const scrollToCommentsSection = useCallback(() => {
    if (commentsRef.current) {
      commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Auto-scroll to comments if coming from notification
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('scrollToComments') === 'true' && commentsRef.current) {
      setTimeout(scrollToCommentsSection, 500);
    }
  }, [location, comments, scrollToCommentsSection]);

  const openImageOverlay = (imageUrl, imageAlt, type, youtubeId) => {
    const index = imageArray.findIndex(img => img.url === imageUrl);
    setOverlayImage({
      isOpen: true,
      url: imageUrl,
      alt: imageAlt,
      index: index >= 0 ? index : 0,
      type: type || 'image',
      youtubeId: youtubeId || null
    });
  };

  const closeImageOverlay = () => {
    setOverlayImage({ isOpen: false, url: '', alt: '', index: 0, type: 'image', youtubeId: null });
  };

  const navigateToNextImage = () => {
    if (overlayImage.index < imageArray.length - 1) {
      const nextIndex = overlayImage.index + 1;
      const nextImage = imageArray[nextIndex];
      setOverlayImage({
        isOpen: true,
        url: nextImage.url,
        alt: nextImage.alt,
        index: nextIndex,
        type: nextImage.type || 'image',
        youtubeId: nextImage.youtubeId || null
      });
    }
  };

  const navigateToPrevImage = () => {
    if (overlayImage.index > 0) {
      const prevIndex = overlayImage.index - 1;
      const prevImage = imageArray[prevIndex];
      setOverlayImage({
        isOpen: true,
        url: prevImage.url,
        alt: prevImage.alt,
        index: prevIndex,
        type: prevImage.type || 'image',
        youtubeId: prevImage.youtubeId || null
      });
    }
  };

  const handleReportListing = () => {
    openContact(
      {
        category: 'report_listing',
        subject: `Report Listing - ${pedal.make} ${pedal.model}`,
        message: `Please describe the issue with this listing:\n\n`,
        context: {
          listingId: pedal.id,
          make: pedal.make || 'N/A',
          model: pedal.model || 'N/A',
          owner: ownerInfo.username || 'Unknown',
          ownerId: pedal.ownerId || 'N/A',
          price: pedal.price ? `$${pedal.price}` : 'N/A',
          condition: pedal.condition || 'N/A',
          effectType: pedal.effectTypes && pedal.effectTypes.length > 0 ? pedal.effectTypes.join(', ') : 'N/A',
          location: pedal.location || 'N/A',
          topImage: pedal.images?.top || 'No image',
          reportingUser: currentUser ? firestoreUser?.username || currentUser.email : 'Not logged in',
          timestamp: new Date().toLocaleString()
        }
      },
      async (submitted) => {
        if (!submitted?.message?.trim()) return;
        try {
          const text = submitted.message.trim();
          await addDoc(collection(db, 'listingReports'), {
            listingId: pedal.id,
            listingMake: pedal.make || 'N/A',
            listingModel: pedal.model || 'N/A',
            listingPrice: pedal.price || null,
            listingCondition: pedal.condition || 'N/A',
            listingOwner: ownerInfo.username || 'Unknown',
            listingOwnerId: pedal.ownerId || 'N/A',
            reportedBy: currentUser ? currentUser.uid : 'anonymous',
            reportedByUsername: currentUser ? firestoreUser?.username || currentUser.email : 'Not logged in',
            reportedByEmail: currentUser?.email || 'Not logged in',
            userComment: text,
            reason: text,
            status: 'pending', // pending, investigating, resolved, dismissed
            priority: 'medium', // low, medium, high, urgent
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            adminNotes: null,
            resolvedBy: null,
            resolvedAt: null
          });
          console.log('📝 Listing reported and logged to Firestore for admin review');
        } catch (e) {
          console.error('❌ Failed to log listing report to Firestore:', e);
        }
      }
    );
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      setCommentError('Please sign in to leave a comment.');
      return;
    }

    if (!newComment.trim()) {
      return;
    }

    // Validate the message, not the people addressed. Only names that can
    // legitimately be addressed here are exempt (owner, commenters, @ popup
    // picks); see stripMentions for why the exemption is not syntax-based.
    const validation = validateMessage(stripMentions(newComment, mentionExemptions()));
    if (!validation.isValid) {
      setCommentError(validation.error);
      return;
    }

    setCommentError('');
    setSubmittingComment(true);

    try {
      // Extract @mentions from comment — supports @[Multi Word] and @SingleWord
      const mentionRegex = /@\[([^\]]+)\]|@(\w+)/g;
      const mentions = [];
      let match;
      while ((match = mentionRegex.exec(newComment)) !== null) {
        mentions.push(match[1] || match[2]);
      }

      const isOwnerComment = currentUser.uid === pedal.ownerId;
      const commentData = {
        listingId: id,
        listingMake: pedal.make || 'Unknown',
        listingModel: pedal.model || 'Unknown',
        listingOwnerId: pedal.ownerId,
        listingOwnerUsername: ownerInfo.username || 'Unknown',
        userId: currentUser.uid,
        username: firestoreUser?.username || currentUser.email,
        userPhotoURL: firestoreUser?.photoURL || null,
        comment: newComment.trim(),
        mentions: mentions,
        createdAt: serverTimestamp(),
        isVisitorComment: !isOwnerComment,
        ownerReplied: false,
        commentReminderSent: false
      };

      await addDoc(collection(db, 'listingComments'), commentData);
      setNewComment('');
      console.log('💬 Comment posted successfully');
    } catch (err) {
      console.error('❌ Failed to post comment:', err);
      setCommentError('Failed to post comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatCommentTime = (timestamp) => formatRelativeTime(timestamp);

  // Check if pedal matches user's Wantlist
  useEffect(() => {
    const checkWantList = async () => {
      if (!currentUser || !pedal) {
        setCheckingWantList(false);
        return;
      }

      try {
        console.log('🔍 Checking if pedal matches Wantlist...');
        
        // Fetch user's wants
        const wantsQuery = query(
          collection(db, 'wantList'),
          where('uid', '==', currentUser.uid)
        );
        
        const wantsSnapshot = await getDocs(wantsQuery);
        const wants = wantsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        console.log(`📋 Found ${wants.length} wants for user`);
        
        // Strict check: pedal satisfies all want criteria (make, model, effect, price, condition)
        // Use .find() (not .some()) so we capture the matching want's document — the
        // quick-remove path needs its `id` to call deleteDoc.
        const fullMatchWant = wants.find(want => doesPedalFullyMatchWant(want, pedal)) || null;

        // Identity check: a want for this same pedal exists, whatever its other
        // settings. This deliberately does NOT use doesPedalMatchWant, which
        // additionally requires effect types to overlap and now also honours the
        // want's excluded keywords. Both are right for "would this listing
        // interest me?" and wrong for "do I already want this pedal?" — a want
        // tagged Multi FX and a listing tagged Reverb read as unrelated, so the
        // quick-add below wrote a second want for a pedal already on the list.
        // The fuzzy tier below keeps the name matching that catches "Boss SD-1"
        // against "Boss SD-1 Super Overdrive", which the exact identity rule
        // alone would miss.
        // Which of the two tiers matched is tracked, not just whether one did.
        // The exact tier compares normalized make+model, so a hit is the same
        // pedal by definition. The fuzzy tier can genuinely be wrong — it reads
        // "ProCo Rat" and "ProCo Lil Rat" as one pedal — so only that tier is
        // offered an "add it anyway" escape.
        const exactWant = !fullMatchWant
          ? findDuplicateWant(wants, { make: pedal.make, model: pedal.model })
          : null;
        const fuzzyWant = !fullMatchWant && !exactWant
          ? wants.find(want => isSameNamedPedal(want, pedal)) || null
          : null;
        const partialWant = exactWant || fuzzyWant;

        console.log(`✅ Pedal ${fullMatchWant ? 'MATCHES' : partialWant ? 'partially matches (different settings)' : 'does not match'} Wantlist`);
        setExistingFullMatchWant(fullMatchWant);
        setExistingWantSameModel(partialWant || null);
        setExistingWantIsExact(!!exactWant);
        setCheckingWantList(false);

      } catch (error) {
        console.error('❌ Error checking Wantlist:', error);
        setCheckingWantList(false);
      }
    };

    checkWantList();
  }, [currentUser, pedal]);

  // Briefly flash a label on the button after a successful quick add/remove.
  // 1.5s mirrors the "Copied!" feedback on the Share My Pedalboard button so
  // the platform's transient confirmations feel consistent.
  const flashWantFeedback = (label) => {
    setWantFeedback(label);
    setTimeout(() => setWantFeedback(null), 1500);
  };

  // Quick-add path: write a wantList document with only the listing's identity
  // fields (make/model/effectTypes) populated. Price/condition/decade/color are
  // intentionally null so this is a "watch this pedal" bookmark, not a fully-
  // configured want. The user can later open the want from /my-pedals/wants
  // and refine criteria via AddWantForm if they choose.
  //
  // The mechanics live in src/utils/quickWant.js, shared verbatim with the
  // heart badge on listing cards (WANT_DEMAND_BADGE_PLAN.md) — this handler
  // only owns the page's UI state around them.
  const handleQuickAddWant = async () => {
    // Preserve existing signed-out behavior: today the button just navigates
    // to /addpedal with the listing prefilled. Match that exactly so signed-
    // out visitors aren't surprised by a different flow.
    if (!currentUser) {
      // React Router v6 does not auto-scroll on navigation, so without this the
      // user lands on /addpedal at whatever Y offset they had on the listing
      // page (often the bottom, since the want-list button sits in the CTA row
      // near the page footer on mobile).
      window.scrollTo(0, 0);
      navigate(signedOutWantPrefillUrl(pedal), {
        state: {
          tab: 'want',
          returnToListing: {
            id: pedal.id,
            label: `${pedal.make || ''} ${pedal.model || ''}`.trim() || 'this listing'
          }
        }
      });
      return;
    }

    setIsMutatingWantList(true);
    try {
      const { interestResult } = await quickAddWantForListing({
        user: currentUser,
        pedal,
        // Pass known "sent" state through; when false the shared cooldown
        // check (tombstone + Firestore) still guards a remove→re-add resend.
        alreadySentInterest: interestSent || undefined,
        onWantWritten: (wantId, wantData) => {
          // Synthesize the doc shape the want-list-check effect would have
          // stored so the UI flips to State 2 immediately. The Cloud
          // Function-driven matches/wantsMyPedals populate asynchronously.
          setExistingFullMatchWant({ id: wantId, ...wantData });
          flashWantFeedback('Added!');
        }
      });
      if (interestResult === 'sent') setInterestSent(true);
      if (interestResult === 'skipped-no-listings') setHasActiveListing(false);
    } catch (err) {
      console.error('❌ Quick-add to Wantlist failed:', err);
      alert('Could not add to your Wantlist. Please try again.');
    } finally {
      setIsMutatingWantList(false);
    }
  };

  // Has this user already raised their hand on this listing?
  //
  // A query rather than a get on the (deterministic) doc ID: reading a missing
  // document returns permission-denied under the listingInterest rules, whereas
  // a query constrained to fromUserId == me returns an empty set cleanly.
  //
  // "Already" only counts inside the resend window, whatever state the signal is
  // in. An owner who never answers would otherwise lock the sender out of that
  // listing for good, and a pass from either side shouldn't stop them asking
  // again once a board has changed.
  //
  // A reply they haven't acted on is called out in the prompt rather than
  // blocking the button: re-sending replaces the record the reply lives on, so
  // the sender should know it's there before deciding to start over.
  useEffect(() => {
    if (!interestEnabled || !currentUser || !pedal?.id || currentUser.uid === pedal.ownerId) {
      return;
    }
    let cancelled = false;
    getDocs(query(
      collection(db, 'listingInterest'),
      where('fromUserId', '==', currentUser.uid),
      where('listingId', '==', pedal.id)
    ))
      .then((snap) => {
        if (cancelled) return;
        const existing = snap.docs[0]?.data();
        if (!existing) {
          setInterestSent(false);
          return;
        }
        const sentAt = existing.createdAt?.toDate?.();
        setInterestAnswered(existing.status === 'actioned');
        const withinCooldown =
          Boolean(sentAt) && Date.now() - sentAt.getTime() < INTEREST_RESEND_AFTER_MS;
        // A pass from either side ends the round. The hour gate is only for an
        // owner who hasn't answered yet, or a reply the sender hasn't acted on.
        const roundEnded = existing.status === 'dismissed' || existing.status === 'closed';
        setInterestSent(withinCooldown && !roundEnded);
      })
      .catch((err) => console.error('❌ Interest check failed:', err));
    return () => {
      cancelled = true;
    };
  }, [interestEnabled, currentUser, pedal?.id, pedal?.ownerId]);

  // Same query the Account Checklist and the tap-time gate use. Runs only on
  // listings where the interest button can actually appear.
  useEffect(() => {
    if (!interestEnabled || !currentUser || !pedal?.id || currentUser.uid === pedal.ownerId) {
      setHasActiveListing(null);
      return;
    }
    let cancelled = false;
    getDocs(query(
      collection(db, 'listings'),
      where('ownerId', '==', currentUser.uid),
      where('active', '==', true)
    ))
      .then((snap) => {
        if (!cancelled) setHasActiveListing(snap.docs.some((d) => offerModeOf(d.data()) !== 'sell')); // trade-eligible only (plan §5)
      })
      .catch(() => {
        // Unknown on error, which leaves the button enabled — the tap-time
        // check still blocks the send, so a failed read can't leak interest
        // from an empty pedalboard.
        if (!cancelled) setHasActiveListing(null);
      });
    return () => { cancelled = true; };
  }, [interestEnabled, currentUser, pedal?.id, pedal?.ownerId]);

  /**
   * Raise a hand on this listing: notify the owner, and add the pedal to the
   * user's Wantlist as a side effect.
   *
   * The want-list add is additive only. If a want for this model already
   * exists we leave it completely alone rather than reconciling it to the
   * listing — wants are user-authored matcher input, and silently rewriting
   * someone's criteria because they tapped a button would corrupt matches they
   * never asked to change.
   */
  /**
   * Send the interest signal for this listing, if the sender is eligible.
   *
   * Interest is no longer its own button — adding this pedal to your Wantlist
   * IS the interest signal (Jason's call, 2026-08-23: a standalone "Hey, I'm
   * interested" button saw almost no use, while the want-list button it had
   * replaced was already familiar). Every path that expresses "I want this
   * specific pedal" calls this: quick-add, and the conflict modal's update and
   * add-anyway actions.
   *
   * Returns 'sent' | 'skipped-no-listings' | 'skipped-already' | 'error'.
   * Never throws and never alerts: the want-list write is the user's primary
   * intent, so a failed nudge must not present itself as a failed add.
   */
  const sendInterestForPedal = async () => {
    if (!currentUser || !pedal || interestSent) return 'skipped-already';
    // Mechanics shared with the card heart badge (src/utils/quickWant.js):
    // the no-active-listing gate, the delete+set resend on the deterministic
    // doc id, the offerIntentEvents record, and the localStorage tombstone
    // that keeps a remove→re-add inside the hour from re-notifying the owner.
    // Never throws and never alerts — the want write is the primary intent.
    const result = await sendInterestForListing({ user: currentUser, pedal });
    if (result === 'sent') setInterestSent(true);
    if (result === 'skipped-no-listings') {
      // The want still gets written — a Wantlist is a private record and
      // must not require a listing. Only the outbound nudge is withheld, and
      // the "List a pedal first" note under the button explains why.
      setHasActiveListing(false);
    }
    return result;
  };

  const handleRemoveWant = async () => {
    // Defensive: should be guaranteed by button gating (button is only in remove
    // mode when isInWantList === true, which means existingFullMatchWant is set),
    // but check anyway in case of a render-timing edge.
    if (!currentUser || !existingFullMatchWant?.id) return;

    setIsMutatingWantList(true);
    try {
      // Withdraws the interest with the want (Jason's call, 2026-08-23; the
      // shared core in quickWant.js carries the full contract). The server
      // cleanupMatchesOnWantDelete tidies matches/wantsMyPedals — no client
      // work needed.
      const { interestWithdrawn } = await quickRemoveWantForListing({
        user: currentUser,
        pedal,
        wantId: existingFullMatchWant.id
      });
      if (interestWithdrawn) {
        setInterestSent(false);
        setInterestAnswered(false);
      }
      setExistingFullMatchWant(null);
      flashWantFeedback('Removed!');
    } catch (err) {
      console.error('❌ Quick-remove from Wantlist failed:', err);
      alert('Could not remove from your Wantlist. Please try again.');
    } finally {
      setIsMutatingWantList(false);
    }
  };

  // Look up which of my active listings this listing's owner wants.
  // Uses the server-maintained wantsMyPedals collection (same source as the
  // recommendations page) to avoid scanning the owner's full Wantlist.
  useEffect(() => {
    const fetchOwnerWantsForMe = async () => {
      if (!currentUser || !pedal || !pedal.ownerId || pedal.ownerId === currentUser.uid) {
        setOwnerWantsMyPedals([]);
        return;
      }
      try {
        const wmpSnap = await getDocs(
          query(
            collection(db, 'wantsMyPedals'),
            where('pedalOwnerId', '==', currentUser.uid),
            where('wantingUserId', '==', pedal.ownerId)
          )
        );
        if (wmpSnap.empty) {
          setOwnerWantsMyPedals([]);
          return;
        }
        const myPedalIds = Array.from(
          new Set(wmpSnap.docs.map((d) => d.data().pedalId).filter(Boolean))
        );
        const docs = await Promise.all(
          myPedalIds.map((pid) => getDoc(doc(db, 'listings', pid)))
        );
        const wanted = docs
          .filter((d) => d.exists())
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => p.active !== false && !p.inTrade && !p.hiddenFromPedalboard && offerModeOf(p) !== 'sell')
          .map((p) => ({ id: p.id, make: p.make, model: p.model }));
        setOwnerWantsMyPedals(wanted);
      } catch (err) {
        console.error('Error fetching owner-wants-my-pedals:', err);
        setOwnerWantsMyPedals([]);
      }
    };

    fetchOwnerWantsForMe();
  }, [currentUser, pedal]);

  // Render comment text with styled @mentions — supports @[Multi Word] and @SingleWord
  const renderCommentWithMentions = (text) => {
    const parts = text.split(/(@\[[^\]]+\]|@\w+)/g);
    return parts.map((part, index) => {
      if (part.match(/^@\[[^\]]+\]$/) || part.match(/^@\w+$/)) {
        const displayText = part.startsWith('@[') ? '@' + part.slice(2, -1) : part;
        return (
          <span
            key={index}
            style={{
              color: '#5A9ECB',
              fontWeight: '600',
              cursor: 'pointer'
            }}
            className="hover:underline"
          >
            {displayText}
          </span>
        );
      }
      return part;
    });
  };

  // Handle @mention autocomplete
  const handleCommentChange = async (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewComment(value);
    setMentionCursorPosition(cursorPos);

    // Check if user is typing an @mention
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);
      // Only show suggestions if there's no space after @ (active mention)
      if (!textAfterAt.includes(' ') && textAfterAt.length >= 0) {
        setMentionSearchTerm(textAfterAt);
        
        // Fetch users matching the search term
        try {
          // PII isolation: @-mention picker reads sanitized public mirror.
          const usersRef = collection(db, 'publicProfiles');
          let suggestions = [];
          
          if (textAfterAt.length === 0) {
            // Show recent commenters and pedal owner
            const recentUsers = new Set();
            recentUsers.add(pedal.ownerId);
            comments.forEach(c => recentUsers.add(c.userId));
            
            const userPromises = Array.from(recentUsers).slice(0, 4).map(uid =>
              getDoc(doc(db, 'publicProfiles', uid))
            );
            const userDocs = await Promise.all(userPromises);
            suggestions = userDocs
              .filter(d => d.exists())
              .map(d => ({ id: d.id, username: d.data().username }));
          } else {
            // Prefix search on the mirrored lowercase username. This used to
            // download the ENTIRE publicProfiles collection on every keystroke
            // after "@" (1,482 docs on 2026-10-23) and filter in the browser.
            // A range query on one field needs no composite index. The two
            // legacy profiles with no usernameLowercase are not found here.
            const searchLower = textAfterAt.toLowerCase();
            const prefixSnap = await getDocs(query(
              usersRef,
              where('usernameLowercase', '>=', searchLower),
              where('usernameLowercase', '<=', `${searchLower}\uf8ff`),
              orderBy('usernameLowercase'),
              limit(4)
            ));
            suggestions = prefixSnap.docs
              .filter(d => d.data().username)
              .map(d => ({ id: d.id, username: d.data().username }));
          }
          
          setMentionSuggestions(suggestions);
          setShowMentionDropdown(suggestions.length > 0);
          setSelectedSuggestionIndex(0);
        } catch (err) {
          console.error('Error fetching mention suggestions:', err);
        }
      } else {
        setShowMentionDropdown(false);
      }
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Handle keyboard navigation in autocomplete
  const handleKeyDown = (e) => {
    if (!showMentionDropdown) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => 
        Math.min(prev + 1, mentionSuggestions.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && mentionSuggestions.length > 0) {
      e.preventDefault();
      selectMention(mentionSuggestions[selectedSuggestionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  // Insert selected mention — wraps in brackets if username contains spaces
  const selectMention = (user) => {
    const textBeforeCursor = newComment.substring(0, mentionCursorPosition);
    const textAfterCursor = newComment.substring(mentionCursorPosition);
    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
    
    const mentionText = formatMention(user.username);
    addPickedName(user.username);
    const newText =
      textBeforeCursor.substring(0, lastAtSymbol) + 
      mentionText + ' ' + 
      textAfterCursor;
    
    setNewComment(newText);
    setShowMentionDropdown(false);
    
    // Refocus textarea
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        const newCursorPos = lastAtSymbol + mentionText.length + 1;
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const getTradeBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return 0;
    return count; // Show exact number of completed trades, including 0
  };

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>9V</span>
            Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>18V</span>
            Member
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            Free Member
          </div>
        );
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    // Check auth state
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
    });

    // Reset listing-specific state immediately when id changes so the loading
    // fallback fires right away instead of leaving the previous listing on
    // screen until the new data arrives.
    setPedal(null);
    setOwnerInfo({ username: '', badge: null });
    setImageArray([]);
    setExistingFullMatchWant(null);
    setCheckingWantList(true);
    setOwnerWantsMyPedals([]);
    setIsInPurchaseOffer(false);
    setComments([]);
    setLoadingComments(true);
    setDetailLoadProblem(null);

    // Stale-result safety: navigating to another listing (or a Retry re-run)
    // re-fires this effect; the cancelled flag stops the superseded fetch
    // from writing the previous listing's data or a stale failure over the
    // new load.
    let cancelled = false;

    const fetchPedal = async () => {
      try {
        const docRef = doc(db, 'listings', id);
        // The main listing read gets the transient one-shot retry (web only;
        // pass-through on native). An offline CACHE-miss is thrown as
        // unconfirmed rather than read as "deleted" — see throwIfOfflineMiss.
        const snapshot = await withTransientRetry('pedal-details', async () => {
          const s = await getDoc(docRef);
          throwIfOfflineMiss(s, 'pedal-details');
          return s;
        });
        if (cancelled) return;
        if (!snapshot.exists()) {
          // Server-confirmed missing: the honest not-found page, never the
          // eternal spinner this used to be.
          setDetailLoadProblem('notfound');
          return;
        }
        const pedalData = { id: snapshot.id, ...snapshot.data() };
        setPedal(pedalData);
        if (viewRecordedIdRef.current !== pedalData.id) {
          viewRecordedIdRef.current = pedalData.id;
          recordListingView(pedalData.id, pedalData.ownerId);
        }
        const data = snapshot.data();
        if (data.ownerId) {
          // Own try/catch: the pedal is already on screen — a failed owner
          // lookup must degrade to the default avatar, not mark the whole
          // page as failed to load.
          try {
            // PII isolation: read sanitized public mirror.
            // lastActiveAt is intentionally absent from publicProfiles —
            // isWeeklyActiveTrader() now prefers lastActiveWeek for the badge.
            const userSnap = await getDoc(doc(db, 'publicProfiles', data.ownerId));
            if (userSnap.exists() && !cancelled) {
              const userData = userSnap.data();
              const ownerData = {
                username: userData.username || 'Unknown',
                badge: getTradeBadge(userData.completedTrades || 0),
                photoURL: userData.photoURL || '/images/default-avatar.png',
                membershipTier: userData.membershipTier || 'free',
                averageRating: userData.averageRating || 0,
                lastActiveWeek: userData.lastActiveWeek || null,
                responderBadge: userData.responderBadge || null
              };
              setOwnerInfo(ownerData);
            }
          } catch (ownerErr) {
            console.error('Owner profile load failed (pedal still renders):', ownerErr);
          }
        }

        // Only hide action buttons when a purchase offer has been accepted.
        // Read from the listing's own inTrade flag (2026-10-30): purchaseOffers
        // is party-only now, and the flag already tracks exactly this state.
        // functions/markPedalsInPurchase.js sets inTrade when a purchase offer
        // becomes `accepted` and clears it on complete/completed/cancelled,
        // which is the same set of states the removed query covered.
        if (!cancelled) setIsInPurchaseOffer(pedalData?.inTrade === true);
      } catch (err) {
        console.error('Failed to load listing:', err);
        if (!cancelled) setDetailLoadProblem('failed');
      }
    };

    fetchPedal();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [id, detailRetryNonce]);

  // One-shot reconnect recovery for the failed state only (same tested
  // mechanism as Browse): the first online/tab-visible signal re-runs the
  // load once; a run that fails again re-arms a fresh one-shot. Disarms on
  // unmount and when the failed state clears. 'notfound' never arms — it is
  // server-confirmed and a reconnect cannot change it.
  useEffect(() => {
    if (detailLoadProblem !== 'failed') return undefined;
    return armRecoveryOnReconnect(() => setDetailRetryNonce((n) => n + 1));
  }, [detailLoadProblem]);

  if (!pedal) {
    if (detailLoadProblem === 'notfound') {
      // Server-confirmed: the listing genuinely doesn't exist (deleted, or a
      // dead link). Until 2026-09-29 this case spun on "Loading pedal
      // details..." forever.
      return (
        <div className='p-4' style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', color: '#4A5759', maxWidth: '420px', margin: '0 auto 1.25rem auto', lineHeight: 1.5 }}>
            This listing is no longer available.
          </p>
          <Link
            to='/latest-pedals'
            state={{ fresh: true }}
            style={{
              display: 'inline-block',
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1rem',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: '#4A9FCA',
              borderRadius: '50px',
              padding: '0.6rem 2rem',
              textDecoration: 'none'
            }}
          >
            Browse pedals
          </Link>
        </div>
      );
    }
    if (detailLoadProblem === 'failed') {
      return (
        <div className='p-4' style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', color: '#4A5759', maxWidth: '420px', margin: '0 auto 1.25rem auto', lineHeight: 1.5 }}>
            Couldn&rsquo;t load this pedal. Check your connection and try again.
          </p>
          <button
            onClick={() => reloadPage()}
            style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1rem',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: '#4A9FCA',
              border: 'none',
              borderRadius: '50px',
              padding: '0.6rem 2rem',
              cursor: 'pointer'
            }}
          >
            Try again
          </button>
        </div>
      );
    }
    return <div className='p-4'>Loading pedal details...</div>;
  }

  const seoTitle = `${pedal.make} ${pedal.model} — ${offerModeOf(pedal) === 'trade' ? 'For Trade' : offerModeOf(pedal) === 'sell' ? 'For Sale' : 'Trade or Buy'}`;
  const seoDescription = `${pedal.make} ${pedal.model}${pedal.condition ? ` in ${pedal.condition} condition` : ''}${pedal.price ? `. $${pedal.price}` : ''}. ${offerModeOf(pedal) === 'trade' ? 'Available for trade' : offerModeOf(pedal) === 'sell' ? 'Available to buy' : 'Available for trade or purchase'} on PedalxChange.`;

  return (
    <>
    <SEO
      title={seoTitle}
      description={seoDescription}
      image={pedal.images?.top || undefined}
      url={`https://pedalxchange.com/listing/${id}`}
      type="product"
      keywords={`${pedal.make}, ${pedal.model}, trade ${pedal.make} ${pedal.model}, buy ${pedal.make}, guitar pedal, effects pedal${pedal.effectTypes?.length ? ', ' + pedal.effectTypes.join(', ') : ''}`}
      structuredData={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: `${pedal.make} ${pedal.model}`,
        description: seoDescription,
        image: [
          pedal.images?.top,
          pedal.images?.back,
          pedal.images?.upper,
          pedal.images?.lower,
          pedal.images?.detail1,
          pedal.images?.detail2,
          pedal.images?.detail3,
          pedal.images?.detail4
        ].filter(Boolean).length > 0
          ? [pedal.images?.top, pedal.images?.back, pedal.images?.upper, pedal.images?.lower, pedal.images?.detail1, pedal.images?.detail2, pedal.images?.detail3, pedal.images?.detail4].filter(Boolean)
          : [placeholderFor(pedal).url.startsWith('/') ? `https://pedalxchange.com${placeholderFor(pedal).url}` : placeholderFor(pedal).url],
        brand: { '@type': 'Brand', name: pedal.make },
        itemCondition: 'https://schema.org/UsedCondition',
        offers: {
          '@type': 'Offer',
          price: pedal.price || undefined,
          priceCurrency: 'USD',
          availability: pedal.inTrade ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
          url: `https://pedalxchange.com/listing/${id}`
        }
      }}
    />
    <div {...swipeBackProps} className={`pedal-details-page ${swipeBackDragClass} ${swipeBackExitClass}`} style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '0 1rem', overflowX: 'hidden' }}>
      <style jsx>{`
        /* BottomTabBar adds body { padding-bottom: 150px } on mobile to clear the
           fixed tab bar. The body's default white bg shows in that padding band
           below our beige page wrapper. Pin a 150px beige stripe to the BOTTOM
           of the viewport (background-attachment: fixed) so only that band is
           tinted — the area near the navbar stays white. Scoped to this
           component's lifetime; body bg reverts when user navigates away. */
        body {
          background:
            linear-gradient(to bottom,
              #ffffff 0%,
              #ffffff calc(100% - 150px),
              #F7F3EB calc(100% - 150px),
              #F7F3EB 100%) fixed !important;
        }
        @media (max-width: 768px) {
          .pedal-details-page {
            padding: 0 0.5rem !important;
          }
          .pedal-info-box {
            padding: 1rem !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .pedal-top-section {
            flex-direction: column !important;
            gap: 0 !important;
            /* Override the inline marginBottom: '2rem' — that 32px was the
               dominant gap between Active Trader text and Description on mobile. */
            margin-bottom: 0.5rem !important;
          }
          /* The hero wrapper is the positioning context for the Active
             Trader and demand-heart badges. Without an explicit width it
             sized to the image's intrinsic width and overflowed the
             viewport on mobile — the image bled off the right edge and
             both corner badges (anchored to the wrapper's edges) sat far
             from the visible corners, or off-screen entirely. */
          .pedal-hero-wrapper {
            width: 100% !important;
            max-width: 100% !important;
            /* Reserve the hero's space before the photo arrives (2026-10-31):
               a square sized to the viewport width, so the details below no
               longer move down when the image loads. The WHOLE photo is shown
               inside it (contain, no cropping); a portrait or landscape photo
               leaves some of the square's background visible beside it. */
            aspect-ratio: 1 / 1;
            overflow: hidden;
          }
          .pedal-large-image {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            /* !important: the element carries an inline objectFit: cover for desktop. */
            object-fit: contain !important;
          }
          .pedal-small-images-row {
            display: flex !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
            margin-top: 1rem !important;
            /* Reduced from 2.5rem — combined with Description's marginTop:1rem the
               original spacing (~56px) read as too much vertical air on mobile. */
            margin-bottom: 0.5rem !important;
            width: 100% !important;
          }
          /* When this listing has no back/upper/lower/detail images, the row renders
             as an empty div but still applies its margins (~24px of dead space).
             :empty hides it entirely so the gap collapses. */
          .pedal-small-images-row:empty {
            display: none !important;
            margin: 0 !important;
          }
          /* Tighten the gap above the Description heading on mobile.
             Description's inline marginTop: 1rem becomes 0 on mobile via this override. */
          .pedal-description-section {
            margin-top: 0 !important;
          }
          .pedal-ask-question-btn {
            font-size: 1rem !important;
          }
          .pedal-small-images-row > div {
            width: 70px !important;
            /* Reserve the thumbnail box before its image arrives (2026-10-31):
               an unloaded <img> measured 44px tall here, 70px once loaded, and
               the 26px difference pushed the price/condition lines down. */
            height: 70px !important;
            overflow: hidden !important;
            flex-shrink: 0 !important;
            margin-right: calc((100% - (70px * 4)) / 3) !important;
          }
          .pedal-small-images-row > div:nth-child(4n) {
            margin-right: 0 !important;
          }
          .pedal-small-images-row img {
            width: 70px !important;
            height: 70px !important;
            display: block !important;
            object-fit: cover !important;
          }
          .no-comments-placeholder {
            padding: 0 0 0.75rem !important;
            margin-bottom: 0.75rem !important;
          }
          .comments-section-wrapper {
            margin-top: 1rem !important;
            padding-top: 1rem !important;
          }
          /* Ensure the page wrapper always fills the mobile viewport so its beige
             background covers the area between the footer and the bottom tab bar
             (otherwise the body's default white background bleeds through). */
          .pedal-details-page {
            min-height: 100vh !important;
          }
          /* Pin the footer to the bottom of the wrapper when there's extra space,
             so we don't get an awkward beige gap below the footer on tall screens. */
          .pedal-details-footer {
            margin-top: auto !important;
          }
          .mobile-profile-make-container {
            display: grid !important;
            grid-template-columns: 1fr auto !important;
            /* Inter-column gap reduced from 2rem so the right-side profile
               column gets a slightly larger container. The compact
               PriceIndicator pill in the left column (190px wide) was
               pushing the left column's min-content size up, which in
               turn squeezed the right column to its max-width cap and
               caused long usernames to wrap awkwardly. */
            gap: 1.25rem !important;
            align-items: flex-start !important;
            margin-bottom: 0.5rem !important;
          }
          .mobile-left-column {
            grid-column: 1 !important;
            display: flex !important;
            flex-direction: column !important;
            gap: 1rem !important;
          }
          .mobile-make-model-section {
            margin: 0 !important;
          }
          .mobile-make-model-section h2 {
            margin-bottom: 0.35rem !important;
          }
          .mobile-profile-section {
            flex-shrink: 0 !important;
            grid-column: 2 !important;
            /* Bounded card, left-aligned contents (Jason, 2026-10-17): the
               centred stack read as a random floating thing next to the pedal
               details. */
            /* Same box as the Description section below: #d1d5db hairline,
               8px radius, #f9fafb fill. */
            padding: 0.7rem 0.7rem !important;
            border: 1px solid #d1d5db !important;
            border-radius: 8px !important;
            background: #f9fafb !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            align-self: start !important;
            overflow: hidden !important;
            /* Cap width so long usernames wrap inside the card without changing
               column layout. Bumped from 140px to 160px alongside the parent
               grid's gap reduction (2rem → 1.25rem) so the username has a
               slightly larger container and breaks more naturally. */
            min-width: 0 !important;
            max-width: 176px !important;
            box-sizing: border-box !important;
          }
          /* The responder pill must fit inside the card: smaller type, and it
             may wrap onto two lines rather than spill past the edge. */
          .mobile-profile-section .responder-badge-wrap {
            align-self: flex-start !important;
            text-align: left !important;
            width: 100% !important;
          }
          .mobile-profile-section .responder-badge svg {
            width: 22px !important;
            height: 22px !important;
            flex-shrink: 0 !important;
          }
          .mobile-profile-section .responder-badge {
            font-size: 0.75rem !important;
            padding: 3px 9px 3px 8px !important;
            gap: 5px !important;
            white-space: normal !important;
            line-height: 1.2 !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .mobile-username-btn {
            display: block !important;
            text-align: left !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
            line-height: 1.2 !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
          }
          .mobile-membership-badge {
            display: block !important;
            text-align: left !important;
          }
          .mobile-profile-img {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
          .mobile-profile-rating {
            display: flex !important;
            justify-content: flex-start !important;
          }
          .mobile-pedal-details {
            display: block !important;
            grid-column: 1 !important;
            grid-row: 2 !important;
          }
          .mobile-make-model-section {
            flex: 0 0 auto !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            margin-right: 0 !important;
          }
          .mobile-pedal-details {
            display: block !important;
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          .desktop-right-side {
            display: none !important;
          }
          .desktop-small-images {
            display: none !important;
          }
        }
        @media (min-width: 769px) {
          .pedal-small-images-row {
            display: none !important;
          }
          .mobile-profile-make-container {
            display: none !important;
          }
          .mobile-pedal-details {
            display: none !important;
          }
        }
      `}</style>
      
      {/* In Trade/Purchase Indicator */}
      <div className='mb-4 text-center'>
        {pedal.inTrade && (
          <div style={{ 
            display: 'inline-block',
            backgroundColor: '#ea8c00', 
            color: 'white',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            padding: '4px 12px',
            borderRadius: '20px',
            marginTop: '8px',
            marginBottom: '1rem',
            boxShadow: '0 2px 4px rgba(234, 140, 0, 0.3)'
          }}>
            🔒 Currently {pedal.purchaseOfferId ? 'In Purchase' : 'In Trade'}
          </div>
        )}
      </div>

      {/* Pedal Information Box */}
      <div className='pedal-info-box' style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        padding: '2rem',
        paddingBottom: '2.5rem',
        marginBottom: '1.5rem',
        marginTop: '2.5rem',
        maxWidth: '900px',
        width: '100%',
        margin: '2.5rem auto 1.5rem auto'
      }}>
        
        {/* Top Section: Large Top Image on Left, User Profile + Pedal Details on Right */}
        <div className="pedal-top-section" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', marginBottom: '2rem' }}>
          
          {/* Large Top Image - Left Side */}
          {pedal.images?.top && (
            <div 
              className="pedal-hero-wrapper"
              onClick={() => openImageOverlay(pedal.images.top, 'Top View')}
              style={{ 
                cursor: 'pointer',
                flexShrink: 0,
                position: 'relative'
              }}
            >
              <img
                className="pedal-large-image"
                src={pedal.images.top}
                alt='Top View'
                style={{
                  width: '480px',
                  height: '480px',
                  objectFit: 'cover',
                  borderRadius: '10px',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.02)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <ActiveTraderBadge user={ownerInfo} className="active-trader-badge--hero" />
              {/* Demand heart — controlled mode: the page's own three-way
                  handler (conflict modal → remove → add) does the work so the
                  badge and the Save to Wantlist button can never disagree.
                  For the owner, onPress stays null and the badge falls back
                  to its non-interactive count-only rendering. */}
              <WantDemandBadge
                pedal={pedal}
                currentUser={currentUser}
                firestoreUser={firestoreUser}
                onPress={currentUser && pedal.ownerId === currentUser.uid ? null : () => {
                  if (existingWantSameModel) {
                    setShowWantConflictModal(true);
                    return;
                  }
                  if (isInWantList) {
                    handleRemoveWant();
                    return;
                  }
                  handleQuickAddWant();
                }}
                filled={isInWantList}
                busy={checkingWantList || isMutatingWantList}
              />
            </div>
          )}
          
          {/* Placeholder hero for a listing created without photos (plan §4) */}
          {!pedal.images?.top && (
            <div className="pedal-hero-wrapper" style={{ flexShrink: 0, position: 'relative' }}>
              <ListingThumb
                className="pedal-large-image"
                src={placeholderFor(pedal).url}
                fallbackSrc={SKETCH_PLACEHOLDER_URL}
                alt="Photos coming"
                style={{ width: '480px', height: '480px', objectFit: 'cover', borderRadius: '10px', background: '#F5F1E8' }}
              />
              <div
                style={{ position: 'absolute', top: '16px', left: '16px', background: 'rgba(255,255,255,0.95)', color: '#4A5759', fontFamily: 'Nunito, sans-serif', fontSize: '12px', fontWeight: 700, letterSpacing: '0.3px', padding: '5px 10px', borderRadius: '9999px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', zIndex: 10 }}
                title="The owner listed this pedal without photos. Real photos are required before an offer can be accepted."
              >
                Photos coming
              </div>
              {placeholderFor(pedal).source === 'reverb' && placeholderFor(pedal).link && (
                // Required link back for Reverb catalog imagery (plan §3).
                <a
                  href={placeholderFor(pedal).link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ position: 'absolute', bottom: '10px', right: '12px', fontFamily: 'Nunito, sans-serif', fontSize: '11px', color: '#5A9ECB', background: 'rgba(255,255,255,0.9)', padding: '2px 8px', borderRadius: '9999px', textDecoration: 'none' }}
                >
                  Photo: Reverb
                </a>
              )}
              <ActiveTraderBadge user={ownerInfo} className="active-trader-badge--hero" />
            </div>
          )}

          {/* Small Images Row - Mobile Only */}
          <div className="pedal-small-images-row">
            {pedal.images?.back && (
              <div onClick={() => openImageOverlay(pedal.images.back, 'Back View')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.back} alt='Back View' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {pedal.images?.upper && (
              <div onClick={() => openImageOverlay(pedal.images.upper, 'Upper View')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.upper} alt='Upper View' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {pedal.images?.lower && (
              <div onClick={() => openImageOverlay(pedal.images.lower, 'Lower View')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.lower} alt='Lower View' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {pedal.images?.detail1 && (
              <div onClick={() => openImageOverlay(pedal.images.detail1, 'Detail 1')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.detail1} alt='Detail 1' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {pedal.images?.detail2 && (
              <div onClick={() => openImageOverlay(pedal.images.detail2, 'Detail 2')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.detail2} alt='Detail 2' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {pedal.images?.detail3 && (
              <div onClick={() => openImageOverlay(pedal.images.detail3, 'Detail 3')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.detail3} alt='Detail 3' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {pedal.images?.detail4 && (
              <div onClick={() => openImageOverlay(pedal.images.detail4, 'Detail 4')} style={{ cursor: 'pointer', width: '70px', flexShrink: 0 }}>
                <ListingThumb src={pedal.images.detail4} alt='Detail 4' style={{ borderRadius: '6px', objectFit: 'cover' }} />
              </div>
            )}
            {(() => {
              const ytId = getYouTubeId(pedal.youtubeUrl);
              if (!ytId) return null;
              return (
                <div
                  onClick={() => openImageOverlay(`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`, 'YouTube Demo', 'youtube', ytId)}
                  style={{ cursor: 'pointer', width: '70px', flexShrink: 0, position: 'relative' }}
                >
                  <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt='YouTube Demo' style={{ borderRadius: '6px', objectFit: 'cover', width: '70px', height: '70px' }} />
                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: '24px', height: '24px', backgroundColor: 'rgba(0,0,0,0.7)', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
                  }}>
                    <div style={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: '9px solid white', marginLeft: '2px' }} />
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Mobile: Profile on Left, Make/Model on Right */}
          <div className="mobile-profile-make-container">
            {/* Left Column: Make/Model + Pedal Details */}
            <div className="mobile-left-column">
              {/* Mobile Make/Model Section */}
              <div className="mobile-make-model-section" style={{ lineHeight: '1.0', textAlign: 'left' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 1rem 0', padding: '0', lineHeight: '1.0', color: '#374151' }}>
                  {pedal.make}
                </h2>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#6b7280', margin: '0', padding: '0', lineHeight: '1.0' }}>
                  {pedal.model}
                </h3>
              </div>
              
              {/* Mobile Pedal Details (below make/model on left) */}
              <div className="mobile-pedal-details" style={{ fontSize: '1.05rem', color: '#374151', textAlign: 'left' }}>
                {pedal.price && (
                  // MOBILE: price on its own line, Market-price pill ALWAYS on
                  // the line below it (owner call 2026-10-09 — the wrapping
                  // row made the pill's position depend on viewport width).
                  // PriceIndicator self-hides (returns null) when there's no
                  // Reverb data, so the column degrades cleanly to just the
                  // price text.
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', rowGap: '0.5rem', margin: '0 0 0.75rem 0' }}>
                    <PedalPriceDisplay
                      price={Number(pedal.price)}
                      previousPrice={pedal.previousPrice != null ? Number(pedal.previousPrice) : null}
                      layout="detail"
                    />
                    <PriceIndicator
                      compact
                      make={pedal.make}
                      model={pedal.model}
                      condition={pedal.condition}
                      price={Number(pedal.price) || 0}
                      decade={pedal.decade}
                      listingId={pedal.id}
                    />
                    {/* Offer mode (plan §5): its own row under the market price on mobile. */}
                    <OfferModePill mode={offerModeOf(pedal)} large />
                  </div>
                )}
                {pedal.condition && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Condition:</strong> {pedal.condition}</p>
                )}
                {pedal.effectTypes && pedal.effectTypes.length > 0 && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}>
                    <strong>Effect Type{pedal.effectTypes.length > 1 ? 's' : ''}:</strong> {pedal.effectTypes.join(', ')}
                  </p>
                )}
                {pedal.decade && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Decade:</strong> {pedal.decade}</p>
                )}
                {pedal.color && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Color:</strong> {pedal.color}</p>
                )}
                {(
                  (currentUser && pedal.ownerId === currentUser.uid && firestoreUser?.shippingAddress?.city && firestoreUser?.shippingAddress?.state) ||
                  pedal.location
                ) && (
                  <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Location:</strong> {
                    currentUser && pedal.ownerId === currentUser.uid && firestoreUser?.shippingAddress?.city && firestoreUser?.shippingAddress?.state
                      ? `${firestoreUser.shippingAddress.city}, ${firestoreUser.shippingAddress.state}`
                      : pedal.location
                  }</p>
                )}
                
                {/* Owner of a photo-less listing: straight to the edit form's photo step */}
                {currentUser && currentUser.uid === pedal.ownerId && isPlaceholderListing(pedal) && (
                  <button
                    onClick={() => navigate(`/pedalboard?edit=${encodeURIComponent(pedal.id)}&step=photos`)}
                    style={{
                      marginTop: '1rem', padding: '0.6rem 1.25rem', borderRadius: '25px', border: 'none',
                      background: '#C89F3C', color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: 700,
                      fontSize: '1rem', cursor: 'pointer', display: 'block'
                    }}
                  >
                    Add photos
                  </button>
                )}

                {/* Report This Listing Button */}
                <button
                  onClick={handleReportListing}
                  style={{
                    marginTop: '1rem',
                    backgroundColor: 'transparent',
                    color: '#DC2626',
                    border: '1px solid #DC2626',
                    borderRadius: '6px',
                    padding: '6px 12px',
                    fontSize: '0.875rem',
                    fontFamily: 'Nunito, sans-serif',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#DC2626';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#DC2626';
                  }}
                >
                  Report This Listing
                </button>
                
                {/* Active Trader Text */}
                {isWeeklyActiveTrader(ownerInfo) && (
                  <p style={{
                    marginTop: '0.75rem',
                    fontSize: '0.875rem',
                    color: '#87A333',
                    fontWeight: '600',
                    lineHeight: '1.4',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <img 
                      src="/images/Active_Trader.png" 
                      alt="Active Trader"
                      style={{
                        width: '20px',
                        height: '20px',
                        display: 'inline-block'
                      }}
                    />
                    This user has been active in the past week
                  </p>
                )}
              </div>
            </div>
            
            {/* Right Column: Mobile Profile Section */}
            <div className="mobile-profile-section">
              {/* Username */}
              <button
                className="mobile-username-btn"
                onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                style={{ 
                  fontSize: '1.15rem', 
                  color: '#374151', 
                  fontWeight: '700',
                  fontFamily: 'Nunito, sans-serif',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  padding: '0',
                  marginBottom: '0.25rem',
                  wordWrap: 'break-word',
                  whiteSpace: 'normal',
                  textAlign: 'center',
                  maxWidth: '100%',
                  overflowWrap: 'break-word'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {/* Non-breaking space until the owner profile arrives: keeps the line's height so nothing below moves (2026-10-31). */}
                {ownerInfo.username || '\u00a0'}
              </button>
              
              {/* Membership Badge */}
              <div className="mobile-membership-badge" style={{ marginBottom: '0.35rem' }}>
                {getMembershipBadge(ownerInfo.membershipTier)}
              </div>
              
              {/* Profile Image with Badges */}
              <div className="mobile-profile-img" style={{ position: 'relative', width: '50px', height: '50px', marginBottom: '0.35rem' }}>
                <button
                  onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    border: '2px solid #D1D5DB',
                    cursor: 'pointer',
                    padding: '0',
                    background: 'none',
                    overflow: 'hidden',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <img
                    src={ownerInfo.photoURL || '/images/default-avatar.png'}
                    alt='User Avatar'
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                </button>
                {getMembershipProfileBadge(ownerInfo.membershipTier)}
                
                {ownerInfo.badge !== null && ownerInfo.badge !== undefined && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-3px',
                      right: '-3px',
                      backgroundColor: 'gold',
                      color: '#000',
                      fontWeight: 'bold',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '0.65rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fff',
                      boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                    }}
                  >
                    {ownerInfo.badge}
                  </div>
                )}
              </div>
              
              {/* User Rating */}
              {ownerInfo.averageRating > 0 ? (
                <div className="mobile-profile-rating" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                  <span style={{ color: '#f59e0b' }}>★</span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    {ownerInfo.averageRating.toFixed(1)}
                  </span>
                </div>
              ) : (
                <div className="mobile-profile-rating" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  No ratings yet
                </div>
              )}
              <div className="responder-badge-wrap" style={{ marginTop: '0.5rem' }}><ResponderBadge profile={ownerInfo} /></div>
            </div>
          </div>
          
          {/* Right Side: User Profile + Pedal Details */}
          <div className="desktop-right-side" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            
            {/* Owner Profile Section */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              
              {/* Username */}
              <button
                onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                style={{ 
                  fontSize: '1.15rem', 
                  color: '#374151', 
                  textAlign: 'left',
                  fontWeight: '700',
                  fontFamily: 'Nunito, sans-serif',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  padding: '0',
                  marginBottom: '0.25rem'
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {/* Non-breaking space until the owner profile arrives: keeps the line's height so nothing below moves (2026-10-31). */}
                {ownerInfo.username || '\u00a0'}
              </button>
              
              {/* Membership Badge */}
              <div style={{ marginBottom: '0.35rem' }}>
                {getMembershipBadge(ownerInfo.membershipTier)}
              </div>
              
              {/* Profile Image with Badges */}
              <div style={{ position: 'relative', width: '50px', height: '50px', marginBottom: '0.35rem' }}>
                <button
                  onClick={() => navigate(`/profile/${pedal.ownerId}`)}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    border: '2px solid #D1D5DB',
                    cursor: 'pointer',
                    padding: '0',
                    background: 'none',
                    overflow: 'hidden',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                >
                  <img
                    src={ownerInfo.photoURL || '/images/default-avatar.png'}
                    alt='User Avatar'
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                </button>
                {/* Membership Badge - Top Left */}
                {getMembershipProfileBadge(ownerInfo.membershipTier)}
                
                {/* Trade Count Badge - Bottom Right */}
                {ownerInfo.badge !== null && ownerInfo.badge !== undefined && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: '-3px',
                      right: '-3px',
                      backgroundColor: 'gold',
                      color: '#000',
                      fontWeight: 'bold',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '0.65rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid #fff',
                      boxShadow: '0 0 5px rgba(0,0,0,0.3)'
                    }}
                  >
                    {ownerInfo.badge}
                  </div>
                )}
              </div>
              
              {/* User Rating */}
              {ownerInfo.averageRating > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                  <span style={{ color: '#f59e0b' }}>★</span>
                  <span style={{ fontWeight: '600', color: '#374151' }}>
                    {ownerInfo.averageRating.toFixed(1)}
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  No ratings yet
                </div>
              )}
              <div className="responder-badge-wrap" style={{ marginTop: '0.5rem' }}><ResponderBadge profile={ownerInfo} /></div>
            </div>
            
            {/* Pedal Brand and Model */}
            <div style={{ lineHeight: '1.0', textAlign: 'left', marginTop: '1.25rem', marginBottom: '1.75rem' }}>
              <h2 style={{ fontSize: '1.875rem', fontWeight: 'bold', margin: '0 0 0.15rem 0', padding: '0', lineHeight: '1.0', color: '#374151' }}>
                {pedal.make}
              </h2>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#6b7280', margin: '0', padding: '0', lineHeight: '1.0' }}>
                {pedal.model}
              </h3>
            </div>
            
            {/* Pedal Details */}
            <div style={{ fontSize: '1.05rem', color: '#374151', textAlign: 'left' }}>
              {pedal.price && (
                // Flex row: price text + compact Reverb-comparison pill.
                // `flexWrap` lets the pill drop to a second line on narrow
                // viewports; PriceIndicator self-hides (returns null) when
                // there's no Reverb data or the condition is "New", so the
                // row degrades cleanly to just the price text.
                //
                // columnGap (inline) and rowGap (wrapped) are intentionally
                // different: inline needs more breathing room between the
                // price text and the pill, while the wrapped price→pill
                // gap can stay tight because the triangle visually fills
                // some of that space. The slightly larger bottom margin
                // gives the pill room before the next detail row (Condition)
                // when it wraps, where the pill ends up immediately above it.
                <div style={{ display: 'flex', alignItems: 'center', columnGap: '1.25rem', rowGap: '0.75rem', flexWrap: 'wrap', margin: '0 0 0.75rem 0' }}>
                  <PedalPriceDisplay
                    price={Number(pedal.price)}
                    previousPrice={pedal.previousPrice != null ? Number(pedal.previousPrice) : null}
                    layout="detail"
                  />
                  <PriceIndicator
                    compact
                    make={pedal.make}
                    model={pedal.model}
                    condition={pedal.condition}
                    price={Number(pedal.price) || 0}
                    decade={pedal.decade}
                    listingId={pedal.id}
                  />
                </div>
              )}
              {/* Offer mode (plan §5): its own line below price and market on web. */}
              <div style={{ margin: '0 0 0.75rem 0' }}>
                <OfferModePill mode={offerModeOf(pedal)} large />
              </div>
              {pedal.condition && (
                <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Condition:</strong> {pedal.condition}</p>
              )}
              {pedal.effectTypes && pedal.effectTypes.length > 0 && (
                <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}>
                  <strong>Effect Type{pedal.effectTypes.length > 1 ? 's' : ''}:</strong> {pedal.effectTypes.join(', ')}
                </p>
              )}
              {pedal.decade && (
                <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Decade:</strong> {pedal.decade}</p>
              )}
              {pedal.color && (
                <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Color:</strong> {pedal.color}</p>
              )}
              {(
                (currentUser && pedal.ownerId === currentUser.uid && firestoreUser?.shippingAddress?.city && firestoreUser?.shippingAddress?.state) ||
                pedal.location
              ) && (
                <p style={{ margin: '0 0 6px 0', padding: '0', lineHeight: '1.2' }}><strong>Location:</strong> {
                  currentUser && pedal.ownerId === currentUser.uid && firestoreUser?.shippingAddress?.city && firestoreUser?.shippingAddress?.state
                    ? `${firestoreUser.shippingAddress.city}, ${firestoreUser.shippingAddress.state}`
                    : pedal.location
                }</p>
              )}
              
              {/* Report This Listing Button */}
              <button
                onClick={handleReportListing}
                style={{
                  marginTop: '1rem',
                  backgroundColor: 'transparent',
                  color: '#DC2626',
                  border: '1px solid #DC2626',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  fontSize: '0.875rem',
                  fontFamily: 'Nunito, sans-serif',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#DC2626';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#DC2626';
                }}
              >
                Report This Listing
              </button>
              
              {/* Active Trader Text */}
              {isWeeklyActiveTrader(ownerInfo) && (
                <p style={{
                  marginTop: '0.75rem',
                  fontSize: '0.875rem',
                  color: '#87A333',
                  fontWeight: '600',
                  lineHeight: '1.4',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <img 
                    src="/images/Active_Trader.png" 
                    alt="Active Trader"
                    style={{
                      width: '20px',
                      height: '20px',
                      display: 'inline-block'
                    }}
                  />
                  This user has been active in the past week
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Row of Smaller Images Below */}
        <div className="desktop-small-images" style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'flex-start', 
          gap: '16px', 
          marginBottom: '3rem'
        }}>
        {pedal.images?.back && (
          <div 
            onClick={() => openImageOverlay(pedal.images.back, 'Back View')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.back}
              alt='Back View'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {pedal.images?.upper && (
          <div 
            onClick={() => openImageOverlay(pedal.images.upper, 'Upper View')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.upper}
              alt='Upper View'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {pedal.images?.lower && (
          <div 
            onClick={() => openImageOverlay(pedal.images.lower, 'Lower View')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.lower}
              alt='Lower View'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {/* Additional Detail Images - now part of main image section */}
        {pedal.images?.detail1 && (
          <div 
            onClick={() => openImageOverlay(pedal.images.detail1, 'Detail 1')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.detail1}
              alt='Detail 1'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {pedal.images?.detail2 && (
          <div 
            onClick={() => openImageOverlay(pedal.images.detail2, 'Detail 2')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.detail2}
              alt='Detail 2'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {pedal.images?.detail3 && (
          <div 
            onClick={() => openImageOverlay(pedal.images.detail3, 'Detail 3')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.detail3}
              alt='Detail 3'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {pedal.images?.detail4 && (
          <div 
            onClick={() => openImageOverlay(pedal.images.detail4, 'Detail 4')}
            style={{ cursor: 'pointer', width: '140px', flexShrink: 0 }}
          >
            <ListingThumb
              src={pedal.images.detail4}
              alt='Detail 4'
              style={{
                width: '140px',
                height: '140px',
                objectFit: 'cover',
                borderRadius: '6px',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
        {(() => {
          const ytId = getYouTubeId(pedal.youtubeUrl);
          if (!ytId) return null;
          return (
            <div
              onClick={() => openImageOverlay(`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`, 'YouTube Demo', 'youtube', ytId)}
              style={{ cursor: 'pointer', width: '140px', flexShrink: 0, position: 'relative' }}
            >
              <img
                src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                alt='YouTube Demo'
                style={{
                  width: '140px',
                  height: '140px',
                  objectFit: 'cover',
                  borderRadius: '6px',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'scale(1)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div style={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: '40px', height: '40px', backgroundColor: 'rgba(0,0,0,0.7)', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none'
              }}>
                <div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '14px solid white', marginLeft: '3px' }} />
              </div>
            </div>
          );
        })()}
        </div>

        {/* Description (optional since 2026-10-19; hidden when empty) */}
        {(pedal.description || '').trim() !== '' && (
        <div className="pedal-description-section" style={{ marginTop: '1rem' }}>
          <h4 className='font-semibold mb-3' style={{ textAlign: 'left', paddingLeft: '16px', fontSize: '1.25rem' }}>Description</h4>
          <div style={{
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            padding: '16px',
            backgroundColor: '#f9fafb'
          }}>
            <p className='text-gray-700' style={{
              margin: 0,
              textAlign: 'left',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {pedal.description}
            </p>
            <div
              className="pedal-ask-question-cta"
              style={{
                marginTop: '1.5rem',
                textAlign: 'left'
              }}
            >
              <button
                type="button"
                className="pedal-ask-question-btn"
                onClick={scrollToCommentsSection}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer',
                  color: '#5A9ECB',
                  fontWeight: 600,
                  fontSize: 'calc(1rem + 1pt)',
                  textDecoration: 'underline',
                  fontFamily: 'Nunito, sans-serif'
                }}
              >
                Have a question?
              </button>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.4 }}>
                Ask the owner about favorite settings, condition details, or trade interests.
              </p>
            </div>
          </div>
        </div>
        )}

        {/* Wants your: shown when this listing's owner has want entries
            that match one or more of my active listings. Populated from the
            server-maintained wantsMyPedals collection. */}
        {ownerWantsMyPedals.length > 0 && allowsTrade(pedal) && (
          <div style={{
            marginTop: '0.75rem',
            textAlign: 'left',
            color: '#6B7280',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: 700,
            fontSize: '0.95rem',
            lineHeight: 1.4
          }}>
            <strong>Wants your:</strong>{' '}
            {ownerWantsMyPedals.map((p, idx) => (
              <span key={p.id}>
                <Link
                  to={`/listing/${p.id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'auto' })}
                  style={{ color: '#AC4320', textDecoration: 'underline' }}
                >
                  {p.make} {p.model}
                </Link>
                {idx < ownerWantsMyPedals.length - 1 ? ', ' : ''}
              </span>
            ))}
          </div>
        )}

      {/* Save to Wantlist, trade / purchase offer CTAs (funnel tracked in offerIntentEvents).
          Layout strategy:
            - Desktop: reassurance text → one row of 3 buttons (Wantlist, Propose, Offer) → right-aligned How to trade link.
            - Mobile:  Wantlist (full-width) → reassurance text with inline How to trade → Propose+Offer side-by-side.
          Achieved with a single DOM via flex `order` on the section + `display: contents`
          on .pedal-cta-propose-offer-wrapper so its children flow as flex siblings of
          Wantlist on desktop, but become their own row on mobile. */}
      {currentUser && pedal && currentUser.uid !== pedal.ownerId && !isFromMatch && !isFromOffer && !isInPurchaseOffer && (
        <div className={`pedal-cta-section${interestEnabled ? ' pedal-cta-section--interest' : ''}`} style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
          <style jsx>{`
            /* ---------- Desktop (default) ----------
               Single horizontal row: [Wantlist] [Propose] [Offer] [Reassurance + How to trade →] */
            .pedal-cta-section .pedal-cta-row-top {
              display: flex;
              align-items: center;
              flex-wrap: nowrap;
              gap: 0.75rem 1rem;
              margin-bottom: 1.5rem;
            }
            /* Interest prompt sits above the whole row; the interest button is
               the first item in that row on both layouts, so the two read
               together without needing to nest inside the flex line. */
            .pedal-cta-section .pedal-cta-interest-prompt {
              margin: 0 0 0.75rem;
              font-family: 'Nunito', sans-serif;
              font-size: 0.95rem;
              font-weight: 600;
              color: #374151;
              text-align: left;
            }
            /* Lighter weight than the prompt above it so the two lines read as
               headline + explanation rather than one run-on sentence. */
            /* Desktop: full-width line under the button row. */
            .pedal-cta-section .pedal-cta-list-first-note {
              margin: -0.75rem 0 1.25rem;
              max-width: 460px;
              font-size: 0.85rem;
              line-height: 1.4;
              color: #6b7280;
              font-family: 'Nunito', sans-serif;
              text-align: left;
            }
            .pedal-cta-section .pedal-cta-interest-note {
              display: block;
              margin-top: 0.2rem;
              max-width: 460px;
              font-size: 0.85rem;
              font-weight: 400;
              line-height: 1.4;
              color: #6B7280;
            }
            /* Propose+Offer wrapper "disappears" on desktop so its 2 children flow
               as direct flex children of .pedal-cta-row-top. */
            .pedal-cta-section .pedal-cta-propose-offer-wrapper {
              display: contents;
            }
            /* Uniform button width across all three CTAs on desktop. min-width is
               wide enough to comfortably hold "Propose a trade" plus breathing room. */
            .pedal-cta-section .pedal-cta-want-list-btn,
            .pedal-cta-section .pedal-cta-propose-offer-wrapper > button {
              min-width: 170px;
            }
            /* Reassurance text + inline How to trade link sit at the right end of the row.
               flex: 1 + min-width: 0 lets it absorb remaining space and wrap into multiple
               lines as needed, instead of being pushed onto its own row below the buttons. */
            .pedal-cta-section .pedal-cta-reassurance {
              margin: 0;
              flex: 1 1 0;
              min-width: 0;
              max-width: 320px;
              font-size: 0.8rem;
              line-height: 1.4;
              color: #6B7280;
              font-family: 'Nunito', sans-serif;
              text-align: left;
            }
            .pedal-cta-section .pedal-cta-howto-inline {
              color: #5A9ECB;
              text-decoration: underline;
              font-weight: 600;
              white-space: nowrap;
            }

            /* ---------- Mobile ---------- */
            @media (max-width: 768px) {
              /* Make the section a flex column so children can be reordered via the order property */
              .pedal-cta-section {
                display: flex;
                flex-direction: column;
              }
              /* Collapse the row wrapper so its kids become flex children of section */
              .pedal-cta-section .pedal-cta-row-top {
                display: contents;
              }
              /* order: 0 keeps the prompt above the Wantlist / interest button,
                 which is order: 1 in this reordered column. */
              .pedal-cta-section .pedal-cta-interest-prompt {
                order: 0;
                padding: 0 1rem;
                margin: 0 0 0.5rem;
              }
              /* Wantlist sits left-aligned at natural pill width — not full-width.
                 align-self overrides the section's flex-column stretch default. */
              .pedal-cta-section .pedal-cta-want-list-btn {
                order: 1;
                align-self: flex-start;
                width: auto !important;
                margin: 0 1rem 0.75rem !important;
                padding: 8px 16px !important;
                font-size: 15px !important;
              }
              /* Between the disabled interest button (1) and the reassurance
                 line, which shifts to 3 to make room. */
              .pedal-cta-section .pedal-cta-list-first-note {
                order: 2;
                padding: 0 1rem;
                margin: -0.25rem 0 1rem;
                text-align: left;
                max-width: none;
              }
              .pedal-cta-section .pedal-cta-reassurance {
                order: 3;
                text-align: left;
                padding: 0 1rem;
                margin: 0 0 1rem;
                max-width: none;
                font-size: 0.9rem;
              }
              /* Propose+Offer become a true side-by-side flex row on mobile */
              .pedal-cta-section .pedal-cta-propose-offer-wrapper {
                order: 4;
                display: flex !important;
                flex-direction: row !important;
                gap: 0.5rem;
                padding: 0 1rem;
                margin-bottom: 0;
              }
              /* Equal-width buttons. white-space:nowrap + smaller padding keeps
                 "Propose a trade" on one line on iPhone-class widths. */
              .pedal-cta-section .pedal-cta-propose-offer-wrapper > button {
                flex: 1 1 0 !important;
                min-width: 0 !important;
                width: auto !important;
                margin: 0 !important;
                padding: 11px 8px !important;
                font-size: 0.95rem !important;
                white-space: nowrap !important;
              }

              /* Interest variant: the offer copy belongs to the offer buttons, so
                 it drops below them (order 4) instead of splitting the interest
                 button off from its own explanation. Extra space under the
                 interest button keeps the two rungs from reading as one group.
                 These follow the base rules above to win on source order. */
              .pedal-cta-section--interest .pedal-cta-reassurance {
                order: 4;
                margin: 0.85rem 0 0;
              }
              .pedal-cta-section--interest .pedal-cta-want-list-btn {
                margin-bottom: 1.5rem !important;
              }
            }
          `}</style>

          {interestEnabled && (
            <p className="pedal-cta-interest-prompt">
              {/* An answered interest has a reply sitting on the offers page. Left
                  at "we've let them know" the sender would have no idea. */}
              {interestAnswered ? (
                <>
                  They&apos;ve replied.{' '}
                  <Link to="/trade-center" style={{ color: '#5A9ECB' }}>
                    Make an offer
                  </Link>
                  .
                </>
              ) : interestSent
                ? "Nice! It's on your Wantlist and we've let the owner know."
                : (!allowsTrade(pedal) && isInWantList)
                  ? "Nice! It's on your Wantlist. We'll let you know if another one gets listed."
                  : 'Interested in this pedal? Save it to your Wantlist!'}
              {!interestSent && (
                // Both lines live in one paragraph so the block keeps a single
                // bottom margin whether or not the sub-line is showing.
                // The owner is only told when the sender has something to trade,
                // so the promise is conditional — the "List a pedal first" note
                // under the button covers the other case rather than this line
                // claiming something that won't happen.
                <span className="pedal-cta-interest-note">
                  {!allowsTrade(pedal)
                    ? (isInWantList ? '' : "Save this to your Wantlist, and we'll tell you if another one gets listed.")
                    : hasActiveListing === false
                      ? "It'll match you if another one gets listed."
                      : "We'll tell the owner, and match you if another one gets listed."}
                </span>
              )}
            </p>
          )}

          <div className="pedal-cta-row-top">
            <button
              className="pedal-cta-want-list-btn"
              onClick={() => {
                // Three-way routing:
                //   1. Same make/model already on Wantlist with DIFFERENT criteria
                //      → conflict modal (unchanged from previous behavior).
                //   2. Pedal fully matches an existing want on the user's list
                //      → quick-remove (delete the want).
                //   3. No matching want yet → quick-add (write a new want with
                //      just make/model/effectTypes — handler also handles the
                //      signed-out fallback by navigating to the prefill form
                //      exactly as the previous onClick did).
                if (existingWantSameModel) {
                  setShowWantConflictModal(true);
                  return;
                }
                if (isInWantList) {
                  handleRemoveWant();
                  return;
                }
                handleQuickAddWant();
              }}
              // Disabled while loading the initial want-list check OR while a
              // quick add/remove write is in flight. NOTE: previously the button
              // was also disabled when isInWantList was true (State 2 was a
              // dead-end "you already have it" pill). Now State 2 is the active
              // "tap to remove" state, so it must be clickable.
              disabled={checkingWantList || isMutatingWantList}
              style={{
                backgroundColor: 'transparent',
                // State 2 (on list) → teal mint #2A9D8F border + text.
                // State 1 (not on list) or loading → gray.
                color: checkingWantList ? '#9CA3AF' : (isInWantList ? '#2A9D8F' : '#374151'),
                border: `1px solid ${checkingWantList ? '#D1D5DB' : (isInWantList ? '#2A9D8F' : '#9CA3AF')}`,
                borderRadius: '9999px',
                padding: '7px 16px',
                fontFamily: 'Nunito, sans-serif',
                fontSize: '15px',
                // Loading and in-flight states get not-allowed; State 2 is
                // clickable (it removes), so its cursor is the normal pointer.
                cursor: (checkingWantList || isMutatingWantList) ? 'not-allowed' : 'pointer',
                marginRight: '12px',
                // Slight dim while a mutation is in flight so the user sees the
                // button is "busy" without losing the State 1/State 2 colors.
                opacity: checkingWantList ? 0.7 : (isMutatingWantList ? 0.6 : 1),
                transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease'
              }}
              onMouseEnter={(e) => {
                if (checkingWantList || isMutatingWantList) return;
                // Subtle hover tint. Teal tint when on list, light gray when not.
                e.currentTarget.style.backgroundColor = isInWantList
                  ? 'rgba(42, 157, 143, 0.08)'
                  : '#E5E7EB';
              }}
              onMouseLeave={(e) => {
                if (checkingWantList || isMutatingWantList) return;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {checkingWantList
                ? 'Checking...'
                : (wantFeedback != null
                    ? wantFeedback
                    : (isInWantList ? 'In Wantlist' : 'Save to Wantlist'))}
            </button>

            <div className="pedal-cta-propose-offer-wrapper">
              {/* Offer mode (plan §5): trade-only hides Offer to buy, sale-only hides
                  Propose a trade. The offer pages and firestore.rules enforce the same. */}
              {allowsTrade(pedal) && (
              <button
                onClick={() => {
                  void recordOfferIntentEvent(OFFER_INTENT_KINDS.LISTING_CTA_TRADE, { listingId: pedal.id });
                  window.location.href = `/direct-offer?targetPedalId=${pedal.id}`;
                }}
                style={{
                  backgroundColor: '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '8px 16px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: 'pointer',
                  marginRight: allowsPurchase(pedal) ? '12px' : 0
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Propose a trade
              </button>
              )}
              {allowsPurchase(pedal) && (
              <button
                onClick={() => {
                  void recordOfferIntentEvent(OFFER_INTENT_KINDS.LISTING_CTA_PURCHASE, { listingId: pedal.id });
                  window.location.href = `/purchase-offer?targetPedalId=${pedal.id}`;
                }}
                style={{
                  backgroundColor: '#5A9ECB',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '8px 16px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                onMouseLeave={(e) => e.target.style.opacity = '1'}
              >
                Offer to buy
              </button>
              )}
            </div>

            {/* Reassurance + inline How to trade link — sits at the right end of the
                row on desktop; reordered to between Wantlist and Propose+Offer on mobile.
                With the interest button present it can no longer claim an offer is what
                "starts the conversation" — that's now the lower rung's job — so it
                describes what an offer adds instead, and moves below the offer buttons. */}
            <p className="pedal-cta-reassurance">
              {interestEnabled
                ? "Propose an offer to trade or buy this. Nothing's locked in until you both agree."
                : 'Sending an offer starts the conversation.'}{' '}
              <a className="pedal-cta-howto-inline" href="/how-it-works#direct-trade-offers">
                How to trade →
              </a>
            </p>
          </div>

          {/* Why the Wantlist button adds the want but sends no nudge: an
              interest from an empty pedalboard goes nowhere, so it is withheld
              (the want itself still writes). Sits directly under the button on
              both layouts (mobile gives it order 2, between the button and the
              reassurance line). */}
          {hasActiveListing === false && !interestSent && (
            <p className="pedal-cta-list-first-note">
              List a pedal first so owners can see what you have to trade.{' '}
              <Link
                to="/addpedal"
                onClick={() => window.scrollTo({ top: 0, behavior: 'auto' })}
                style={{ color: '#5A9ECB', textDecoration: 'underline', whiteSpace: 'nowrap' }}
              >
                Add a pedal →
              </Link>
            </p>
          )}
        </div>
      )}

      {/* Keeps the divider off the Back link: the comments heading below it
          already clears 2rem of wrapper padding plus its own default 1em top
          margin, so the space above the line has to be padded to match. */}
      <div className="pedal-back-link-row" style={{ textAlign: 'left', marginTop: '1.5rem', marginBottom: interestEnabled ? '2.25rem' : 0 }}>
        <style jsx>{`
          @media (max-width: 768px) {
            .pedal-back-link-row {
              padding-left: 1rem;
            }
          }
        `}</style>
        <button
          onClick={() => navigate(-1)}
          className='text-blue-600 hover:text-blue-800 hover:underline text-sm'
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'none',
            color: '#5A9ECB'
          }}
        >
          ← Back
        </button>
      </div>
      
      {/* Comments Section */}
      <div 
        className="comments-section-wrapper"
        ref={commentsRef}
        style={{
          marginTop: '2rem',
          paddingTop: '2rem',
          borderTop: '1px solid #e5e7eb'
        }}
      >
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
          color: '#1f2937',
          textAlign: 'left'
        }}>
          Questions & Comments ({comments.length})
        </h3>

        {/* Comments List */}
        {loadingComments ? (
          <div style={{ textAlign: 'left', padding: '0.5rem 0', color: '#6b7280' }}>
            Loading comments...
          </div>
        ) : comments.length === 0 ? (
          <div className="no-comments-placeholder" style={{ textAlign: 'left', padding: '0 0 0.75rem', color: '#6b7280', marginBottom: '0.75rem' }}>
            <p style={{ marginBottom: '0.5rem', fontSize: '1rem' }}>No comments yet</p>
            <p style={{ fontSize: '0.875rem', margin: 0 }}>
              Be the first to ask a question about this pedal!
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
            <style jsx>{`
              @media (max-width: 768px) {
                .comment-item {
                  gap: 0.5rem !important;
                  padding: 0.75rem !important;
                }
                
                .comment-avatar {
                  width: 32px !important;
                  height: 32px !important;
                  min-width: 32px !important;
                }
                
                .comment-content {
                  min-width: 0 !important;
                  flex: 1 !important;
                }

                .comment-reply-row {
                  padding-left: calc(32px + 1rem) !important;
                }
              }
            `}</style>
            {comments.map((comment) => (
              <div
                key={comment.id}
                className="comment-item"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  padding: '1rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}
              >
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Link to={`/profile/${comment.userId}`}>
                    <img
                      src={comment.userPhotoURL || '/images/default-avatar.png'}
                      alt={comment.username}
                      className="comment-avatar"
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        flexShrink: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </Link>
                  <div className="comment-content" style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      flexWrap: 'wrap'
                    }}>
                      <Link 
                        to={`/profile/${comment.userId}`}
                        style={{ 
                          fontWeight: 'bold', 
                          color: '#1f2937',
                          textDecoration: 'none'
                        }}
                        className="hover:underline"
                      >
                        {comment.username}
                      </Link>
                      {comment.userId === pedal.ownerId && (
                        <span style={{
                          backgroundColor: '#5A9ECB',
                          color: 'white',
                          fontSize: '0.65rem',
                          padding: '0.125rem 0.5rem',
                          borderRadius: '999px',
                          fontWeight: 'bold'
                        }}>
                          LISTER
                        </span>
                      )}
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {formatCommentTime(comment.createdAt)}
                      </span>
                    </div>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      textAlign: 'left'
                    }}>
                      {renderCommentWithMentions(comment.comment)}
                    </p>
                  </div>
                </div>
                {/* Reply button, left-aligned with the comment text (indent =
                    avatar width + the row's 1rem gap; the mobile rule above
                    matches the 32px phone avatar). A blue pill (2026-10-06)
                    rather than the old small text link: people were typing
                    answers straight into the box without it, so the person
                    who asked was never @mentioned and never notified.
                    Signed-in users only: signed out there is no comment box
                    to reply in, and the "Please sign in" prompt below says so. */}
                {currentUser && (
                <div className="comment-reply-row" style={{ textAlign: 'left', width: '100%', marginTop: '0.25rem', paddingLeft: 'calc(40px + 1rem)', boxSizing: 'border-box' }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Bracketed when the name has anything beyond letters,
                      // digits or underscore (dash, dot, space, accents), or
                      // the parser truncates it and the wrong person is
                      // notified. Prepend to an existing draft instead of
                      // wiping it; skip if that mention is already there.
                      // Verified profile name ONLY. The username stored on
                      // a comment can be an email fallback, so it is never
                      // inserted. If the profile name isn't available (not
                      // loaded, lookup failed, profile gone), open the box
                      // without a mention.
                      const replyTo = replyTargetName(participantUsernames, comment);
                      if (replyTo) setNewComment((draft) => prependMention(draft, replyTo));
                      textareaRef.current?.focus();
                      // Scroll to comment form
                      setTimeout(() => {
                        textareaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }}
                    style={{
                      background: '#C89F3C',
                      border: 'none',
                      color: '#ffffff',
                      fontSize: '0.8125rem',
                      fontWeight: '700',
                      fontFamily: 'Nunito, sans-serif',
                      cursor: 'pointer',
                      padding: '0.55rem 1.2rem',
                      borderRadius: '50px',
                      lineHeight: 1.2
                    }}
                  >
                    Reply
                  </button>
                </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Comment Form */}
        {currentUser ? (
          <form onSubmit={handleSubmitComment} style={{ marginTop: comments.length > 0 ? '2rem' : '0' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <img
                src={firestoreUser?.photoURL || '/images/default-avatar.png'}
                alt={firestoreUser?.username || 'User'}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  flexShrink: 0
                }}
              />
              <div style={{ flex: 1, position: 'relative' }}>
                <AutoGrowTextarea
                  ref={textareaRef}
                  value={newComment}
                  maxLength={500}
                  maxHeight={220}
                  onChange={(e) => { setCommentError(''); handleCommentChange(e); }}
                  onKeyDown={handleKeyDown}
                  // Kept to one line at phone width. The old placeholder also
                  // carried "(Use @username to mention someone)" and wrapped to
                  // three lines in an 80px box, so the third line was clipped
                  // and the text looked sunk to the bottom (2026-10-06; the
                  // 2341f32 line-height fix was a real iOS fix but not this
                  // one). The @username hint lives in the Tip line below.
                  placeholder="Ask a question or leave a comment..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    // 16px + explicit line-height, matching TradeMessages/DirectOffer:
                    // the global `html.capacitor-native textarea { font-size: 16px !important }`
                    // (src/index.css) was silently resizing this box's text on iOS while its
                    // UA-default line-height stayed computed for 14px, sinking the placeholder.
                    // Authoring 16px here keeps web and native identical (and stops iOS
                    // Safari's zoom-on-focus on the mobile web too).
                    lineHeight: '1.4',
                    display: 'block',
                    fontFamily: 'inherit'
                  }}
                  disabled={submittingComment}
                />
                {(commentError || newComment.length >= 400) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginTop: '4px' }}>
                    <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>{commentError}</span>
                    {newComment.length >= 400 && (
                      <span style={{ fontSize: '0.8rem', color: 500 - newComment.length <= 20 ? '#dc2626' : '#9ca3af', whiteSpace: 'nowrap' }}>
                        {500 - newComment.length} left
                      </span>
                    )}
                  </div>
                )}
                
                {/* Autocomplete Dropdown */}
                {showMentionDropdown && mentionSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: 0,
                    marginBottom: '0.5rem',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '300px',
                    width: '100%',
                    zIndex: 1000
                  }}>
                    {mentionSuggestions.map((user, index) => (
                      <div
                        key={user.id}
                        onClick={() => selectMention(user)}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          backgroundColor: index === selectedSuggestionIndex ? '#f3f4f6' : 'white',
                          borderBottom: index < mentionSuggestions.length - 1 ? '1px solid #e5e7eb' : 'none',
                          fontSize: '0.875rem',
                          fontWeight: '500',
                          color: '#374151'
                        }}
                        onMouseEnter={() => setSelectedSuggestionIndex(index)}
                      >
                        @{user.username}
                      </div>
                    ))}
                  </div>
                )}
                <style jsx>{`
                  .comment-form-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 0.5rem;
                  }
                  
                  @media (max-width: 768px) {
                    .comment-form-footer {
                      flex-direction: column;
                      align-items: flex-start;
                      gap: 0.75rem;
                    }
                    
                    .comment-form-footer button {
                      width: 100%;
                    }
                  }
                `}</style>
                <div className="comment-form-footer">
                  <span style={{ fontSize: '0.9375rem', color: '#6b7280', fontWeight: 'bold' }}>
                    Tip: Use @username to notify someone
                  </span>
                  <button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    style={{
                      padding: '0.5rem 1.5rem',
                      backgroundColor: submittingComment || !newComment.trim() ? '#d1d5db' : '#C89F3C',
                      color: 'white',
                      border: 'none',
                      borderRadius: '25px',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      cursor: submittingComment || !newComment.trim() ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submittingComment ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        ) : (
          <div style={{
            padding: '1.5rem',
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            marginTop: comments.length > 0 ? '2rem' : '0',
            textAlign: 'center'
          }}>
            <p style={{ color: '#92400e', margin: 0 }}>
              Please <Link to="/login" style={{ color: '#5A9ECB', fontWeight: 'bold' }}>sign in</Link> to leave a comment or ask a question.
            </p>
          </div>
        )}
      </div>
      </div>
      
      {/* Report Bug Section */}
      <div className='text-center mt-8 pt-4 border-t border-gray-200 pedal-details-footer'>
        <style jsx>{`
          @media (max-width: 768px) {
            .pedal-details-footer {
              padding-bottom: 100px !important;
            }
          }
        `}</style>
        <p className='text-gray-600 text-sm'>
          Need help or found a bug?{' '}
          <button
            type="button"
            onClick={() => openContact({ category: 'general' })}
            style={{
              color: '#5A9ECB',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              cursor: 'pointer'
            }}
            className='hover:underline'
          >
            Contact Support
          </button>
        </p>
      </div>
      
      {/* Want-list conflict modal: shown when the user has the same make/model
          already in their Wantlist with different settings (e.g. lower max price
          or different condition). Lets them jump straight to editing the existing
          want, or cancel and stay on this page. */}
      {showWantConflictModal && existingWantSameModel && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowWantConflictModal(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            padding: '1rem'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '1.5rem 1.5rem 1.25rem',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)',
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            <h3 style={{
              fontSize: '1.2rem',
              fontWeight: 700,
              color: '#1f2937',
              margin: '0 0 0.5rem'
            }}>
              You have this pedal in your Wantlist
            </h3>
            <p style={{
              fontSize: '0.9rem',
              color: '#4b5563',
              lineHeight: 1.5,
              margin: '0 0 1rem'
            }}>
              {wantConflict?.reason}
            </p>
            <div style={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '0.75rem 0.875rem',
              fontSize: '0.875rem',
              color: '#374151',
              marginBottom: '1.25rem',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              columnGap: '0.75rem',
              rowGap: '0.5rem'
            }}>
              {(wantConflict?.rows || []).map(([label, value]) => (
                <Fragment key={label}>
                  <span style={{ color: '#6B7280' }}>{label}:</span>
                  <span>{value}</span>
                </Fragment>
              ))}
              {/* Thin divider visually separates the user's current criteria from
                  the listing they're looking at. Spans both grid columns. */}
              <div style={{ gridColumn: '1 / -1', height: 1, backgroundColor: '#E5E7EB', margin: '0.25rem 0' }} />
              <span style={{ color: '#6B7280' }}>This listing:</span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                <span>{pedal.price ? `$${pedal.price}` : 'No price'}</span>
                {pedal.condition && <span>{pedal.condition}</span>}
              </span>
            </div>
            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowWantConflictModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  color: '#374151',
                  border: '1px solid #9CA3AF',
                  borderRadius: '9999px',
                  padding: '7px 16px',
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              {/* Only offered when the fuzzy name check is what matched. That
                  tier reads "ProCo Rat" and "ProCo Lil Rat" as the same pedal,
                  so it can be wrong and the user has to be able to overrule it.
                  An exact make+model match cannot be wrong in the same way, and
                  offering an override there would just recreate the duplicate
                  wants this guard exists to prevent. */}
              {!existingWantIsExact && (
                <button
                  onClick={() => {
                    setShowWantConflictModal(false);
                    handleQuickAddWant();
                  }}
                  disabled={isMutatingWantList}
                  style={{
                    backgroundColor: 'transparent',
                    color: '#374151',
                    border: '1px solid #9CA3AF',
                    borderRadius: '9999px',
                    padding: '7px 16px',
                    fontFamily: 'Nunito, sans-serif',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: isMutatingWantList ? 'not-allowed' : 'pointer',
                    opacity: isMutatingWantList ? 0.6 : 1
                  }}
                >
                  It's a different pedal — add it
                </button>
              )}
              <button
                onClick={() => {
                  setShowWantConflictModal(false);
                  // Updating the want to fit this pedal is the same expression
                  // of intent as adding it, so the owner hears about it too
                  // (Jason's call, 2026-08-23). Not awaited: this path
                  // navigates away immediately, and the write does not need
                  // this component alive to finish.
                  void sendInterestForPedal();
                  // Navigate to the WantList page in edit mode for the existing want.
                  // WantList reads location.state.editWantId on mount and opens that
                  // want directly in the AddWantForm at the top of the page.
                  // Also pass:
                  //   - returnToListing: so WantList can show a "Return to this
                  //     listing" prompt after the user saves.
                  //   - editHints: listing context used to fill in fields the
                  //     existing want has left blank (e.g. effectTypes). We never
                  //     override fields the want already has set.
                  // Edit-from-listing flow → standalone Add Want screen in
                  // edit mode for the existing want, with the listing's
                  // criteria forwarded so the form can highlight & auto-fill
                  // the fields that don't currently match.
                  // Same scroll-position-preservation issue as the quick-add
                  // path: without this, /addpedal opens at whatever Y the user
                  // was at when the conflict modal appeared.
                  window.scrollTo(0, 0);
                  navigate(`/addpedal?tab=want&edit=${encodeURIComponent(existingWantSameModel.id)}`, {
                    state: {
                      tab: 'want',
                      editHints: {
                        effectTypes: Array.isArray(pedal.effectTypes) ? pedal.effectTypes : []
                      },
                      // The listing's criteria-relevant fields. AddPedalPage
                      // forwards these to AddWantForm, which compares them
                      // against the existing want (via getWantMismatchFields)
                      // to auto-fill + highlight only the fields that
                      // currently exclude this listing.
                      matchListing: {
                        price: pedal.price ?? null,
                        condition: pedal.condition || '',
                        decade: pedal.decade || '',
                        color: pedal.color || ''
                      },
                      returnToListing: {
                        id: pedal.id,
                        label: `${pedal.make || ''} ${pedal.model || ''}`.trim() || 'this listing'
                      }
                    }
                  });
                }}
                style={{
                  backgroundColor: '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '8px 16px',
                  fontFamily: 'Nunito, sans-serif',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Edit existing want
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Overlay Modal */}
      <ImageOverlay
        isOpen={overlayImage.isOpen}
        imageUrl={overlayImage.url}
        imageAlt={overlayImage.alt}
        onClose={closeImageOverlay}
        onNext={navigateToNextImage}
        onPrev={navigateToPrevImage}
        hasNext={overlayImage.index < imageArray.length - 1}
        hasPrev={overlayImage.index > 0}
        type={overlayImage.type}
        youtubeId={overlayImage.youtubeId}
      />
    </div>

    <ListPedalPromptModal
      isOpen={showListPedalPrompt}
      onClose={() => setShowListPedalPrompt(false)}
    />
    </>
  );
};

export default PedalDetails;
